import challenging from '../assets/icons/pictograms/ic_ponder_challenging.svg';
import relaxed from '../assets/icons/pictograms/ic_ponder_relaxed.svg';
import solid from '../assets/icons/pictograms/ic_ponder_solid.svg';
import maximumEffort from '../assets/icons/pictograms/ic_ponder_maximum_effort.svg';

import snappy from '../assets/icons/pictograms/ic_ponder_snappy.svg';
import rough from '../assets/icons/pictograms/ic_ponder_rough.svg';
import smooth from '../assets/icons/pictograms/ic_ponder_smooth.svg';
import broken from '../assets/icons/pictograms/ic_ponder_broken.svg';

export const pictogramsEnum = {
  1: 'relaxed',
  2: 'solid',
  3: 'challenging',
  4: 'maximum_effort',

  5: 'snappy',
  6: 'smooth',
  7: 'rough',
  8: 'broken',
};

export const pictograms = {
  [pictogramsEnum[1]]: relaxed,
  [pictogramsEnum[2]]: solid,
  [pictogramsEnum[3]]: challenging,
  [pictogramsEnum[4]]: maximumEffort,

  [pictogramsEnum[5]]: snappy,
  [pictogramsEnum[6]]: smooth,
  [pictogramsEnum[7]]: rough,
  [pictogramsEnum[8]]: broken,
};

/**
 * @template {{ value: number }} T
 * @param {T[]} options
 * @param {Record<number, string>} icons
 * @returns {(T & { icon: string })[]}
 */
const withIcons = (options, icons) =>
  options.map((option) => ({ ...option, icon: icons[option.value] }));

export const QUALITY = withIcons(
  [
    {
      value: 4,
      name: 'Snappy',
      description: 'Everything snaps right into place with full control',
    },
    { value: 3, name: 'Smooth', description: 'Controlled and connected with little wasted motion' },
    { value: 2, name: 'Rough', description: 'The movement is happening and needs more practice' },
    {
      value: 1,
      name: 'Clunky',
      description: 'Broken, muscling through - common with new movements',
    },
  ],
  { 4: pictogramsEnum[5], 3: pictogramsEnum[6], 2: pictogramsEnum[7], 1: pictogramsEnum[8] },
);

export const EASE = withIcons(
  [
    {
      value: 4,
      name: 'Relaxed',
      description: 'Good flow, easy breathing, and it all just feels good',
    },
    { value: 3, name: 'Natural', description: 'Everything flows together, even during effort' },
    { value: 2, name: 'Cautious', description: 'Holding back or bracing during unfamiliar moves' },
    { value: 1, name: 'Mechanical', description: 'Movements require deliberate effort and focus' },
  ],
  { 4: pictogramsEnum[1], 3: pictogramsEnum[2], 2: pictogramsEnum[3], 1: pictogramsEnum[4] },
);

export const DENSITY = [
  { value: 4, name: 'Intense', description: 'Full effort. Little rest. Great for conditioning.' },
  {
    value: 3,
    name: 'Heavy',
    description: 'Hard effort with plenty of rest. Builds strength and power.',
  },
  {
    value: 2,
    name: 'Steady',
    description: 'Continuous work at a manageable pace. Builds stamina.',
  },
  {
    value: 1,
    name: 'Chill',
    description: 'Relaxed effort and rest as needed. Ideal for recovery.',
  },
];

/**
 * @param {{ value: number, name: string }[]} options
 * @returns {Record<number, string>}
 */
const labelsByValue = (options) =>
  Object.fromEntries(options.map((option) => [option.value, option.name]));

export const PONDER_EASE = labelsByValue(EASE);
export const PONDER_QUALITY = labelsByValue(QUALITY);
export const PONDER_DENSITY = labelsByValue(DENSITY);

/**
 * In-session ratings, in the order they come up, each asked when its phase ends.
 * @type {{
 *   type: 'quality' | 'ease' | 'density',
 *   label: string,
 *   title: string,
 *   instruction: string,
 *   phase: number,
 *   options: { value: number, name: string, description: string }[],
 * }[]}
 */
export const RATINGS = [
  {
    type: 'quality',
    label: 'Quality',
    title: 'How did you move today?',
    instruction:
      'Quality practice leads to mastery, and the goal is just to get a little smoother with each move every time. How well did you stick to the form cues?',
    phase: 2,
    options: QUALITY,
  },
  {
    type: 'ease',
    label: 'Ease',
    title: 'How did your moves feel?',
    instruction:
      'Play is serious business: variation and combination build resilience and adaptability. How did you feel during this portion of your training?',
    phase: 3,
    options: EASE,
  },
  {
    type: 'density',
    label: 'Density',
    title: 'How much did you move?',
    instruction:
      'Pushing yourself is a balance of capacity and workload. Each option below moves you forward in a different way.',
    phase: 4,
    options: DENSITY,
  },
];

export const MAX_SESSION_TAGS = 3;
